// Simple video compression utilities without FFmpeg
interface CompressionOptions {
  targetBitrate?: number;
  targetResolution?: '720p' | '1080p' | 'original';
  onProgress?: (progress: number) => void;
}

export class VideoCompressor {
  async load(): Promise<void> {
    // No-op for compatibility
  }

  async compressVideo(
    file: File,
    options: CompressionOptions = {}
  ): Promise<File> {
    // Since we can't compress without FFmpeg, return the original file
    // but show a message to the user
    throw new Error(
      'Browser-based video compression is currently unavailable. ' +
      'Please compress your video before uploading using tools like HandBrake or FFmpeg. ' +
      'Recommended settings: 720p resolution, 2.5 Mbps bitrate.'
    );
  }

  async getVideoMetadata(file: File): Promise<{
    duration: number;
    width: number;
    height: number;
    bitrate: number;
  }> {
    // Return estimated metadata
    return {
      duration: 0,
      width: 1280,
      height: 720,
      bitrate: 10000,
    };
  }

  terminate(): void {
    // No-op
  }
}

let compressorInstance: VideoCompressor | null = null;

export function getVideoCompressor(): VideoCompressor {
  if (!compressorInstance) {
    compressorInstance = new VideoCompressor();
  }
  return compressorInstance;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function estimateCompressedSize(
  originalSizeBytes: number,
  currentBitrate: number,
  targetBitrate: number
): number {
  const bitrateRatio = targetBitrate / currentBitrate;
  return Math.round(originalSizeBytes * bitrateRatio);
}